import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Truck, Shield, User, MapPin, Phone, Mail } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { formatPrice } from '../lib/utils';


interface ShippingData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    district: string;
    city: string;
    postalCode: string;
  }
  
  interface PaymentData {
    method: 'card' | 'bank' | 'cash';
    cardNumber: string;
    expiryDate: string;
    cvv: string;
    cardName: string;
  }
  
  const Checkout: React.FC = () => {
    const { items, total, clearCart } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [currentStep, setCurrentStep] = useState(1);
    const [shippingData, setShippingData] = useState<ShippingData>({
      firstName: user?.name?.split(' ')[0] || '',
      lastName: user?.name?.split(' ').slice(1).join(' ') || '',
      email: user?.email || '',
      phone: user?.phone || '',
      address: '',
      district: '',
      city: 'Lima',
      postalCode: ''
    });
    
    const [paymentData, setPaymentData] = useState<PaymentData>({
      method: 'card',
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardName: ''
    });
    
    const [isProcessing, setIsProcessing] = useState(false);
  
    // Si el carrito está vacío, redirigir
    if (items.length === 0) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Tu carrito está vacío</h1>
            <Link to="/productos" className="btn-primary">
              Ir a productos
            </Link>
          </div>
        </div>
      );
    }
  
    const subtotal = total;
    const shipping = 0; // Envío gratis
    const tax = total * 0.18; // IGV 18%
    const finalTotal = subtotal + shipping + tax;
  
    const handleShippingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setShippingData({
        ...shippingData,
        [e.target.name]: e.target.value
      });
    };
  
    const handlePaymentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setPaymentData({
        ...paymentData,
        [e.target.name]: e.target.value
      });
    };
  
    const handleSubmitOrder = async () => {
      setIsProcessing(true);
      
      // Simular procesamiento de pago
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simular éxito del pago
      const orderId = `ORD-${Date.now()}`;
      
      // Limpiar carrito
      clearCart();
      
      // Redirigir a página de confirmación (por ahora un alert)
      alert(`¡Pedido realizado con éxito!\nNúmero de orden: ${orderId}\nTotal: ${formatPrice(finalTotal)}`);
      
      navigate('/perfil');
      setIsProcessing(false);
    };
  
    const districts = [
      'Ate', 'Barranco', 'Breña', 'Callao', 'Cercado de Lima', 'Chorrillos',
      'Jesús María', 'La Molina', 'La Victoria', 'Lince', 'Los Olivos',
      'Magdalena', 'Miraflores', 'Pueblo Libre', 'San Borja', 'San Isidro',
      'San Juan de Lurigancho', 'San Luis', 'San Martín de Porres', 'San Miguel',
      'Surco', 'Surquillo', 'Villa El Salvador', 'Villa María del Triunfo'
    ];
  
    return (
      <div className="min-h-screen bg-infinity-black">
        {/* Header */}
        <div className="bg-infinity-gradient shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Link
                  to="/carrito"
                  className="flex items-center text-infinity-yellow hover:text-infinity-white"
                >
                  <ArrowLeft size={20} className="mr-2" />
                  Volver al carrito
                </Link>
                <h1 className="text-3xl font-bold text-infinity-white">Checkout</h1>
              </div>
              <div className="text-right">
                <p className="text-sm text-infinity-yellow">Total a pagar</p>
                <p className="text-2xl font-bold text-infinity-yellow">{formatPrice(finalTotal)}</p>
              </div>
            </div>
          </div>
        </div>
  
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-8">
              {[
                { step: 1, title: 'Envío', icon: Truck },
                { step: 2, title: 'Pago', icon: CreditCard },
                { step: 3, title: 'Confirmación', icon: Shield }
              ].map(({ step, title, icon: Icon }) => (
                <div key={step} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    currentStep >= step 
                      ? 'bg-infinity-purple text-white' 
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {currentStep > step ? '✓' : <Icon size={20} />}
                  </div>
                  <span className={`ml-2 font-medium ${
                    currentStep >= step ? 'text-infinity-purple' : 'text-gray-600'
                  }`}>
                    {title}
                  </span>
                  {step < 3 && (
                    <div className={`w-16 h-0.5 ml-4 ${
                      currentStep > step ? 'bg-infinity-purple' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
  
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Formulario */}
            <div className="lg:col-span-2">
              {/* Paso 1: Información de Envío */}
              {currentStep === 1 && (
                <div className="bg-infinity-dark rounded-lg shadow-lg p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <Truck size={24} className="text-infinity-yellow" />
                    <h2 className="text-2xl font-bold text-infinity-yellow">Información de Envío</h2>
                  </div>
  
                  <form className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-infinity-yellow mb-2">
                          Nombre *
                        </label>
                        <div className="relative">
                          <User size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            name="firstName"
                            required
                            value={shippingData.firstName}
                            onChange={handleShippingChange}
                            className="w-full pl-10 pr-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                          />
                        </div>
                      </div>
  
                      <div>
                        <label className="block text-sm font-medium text-infinity-yellow mb-2">
                          Apellidos *
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          required
                          value={shippingData.lastName}
                          onChange={handleShippingChange}
                          className="w-full px-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                        />
                      </div>
                    </div>
  
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-infinity-yellow mb-2">
                          Email *
                        </label>
                        <div className="relative">
                          <Mail size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="email"
                            name="email"
                            required
                            value={shippingData.email}
                            onChange={handleShippingChange}
                            className="w-full pl-10 pr-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                          />
                        </div>
                      </div>
  
                      <div>
                        <label className="block text-sm font-medium text-infinity-yellow mb-2">
                          Teléfono *
                        </label>
                        <div className="relative">
                          <Phone size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="tel"
                            name="phone"
                            required
                            value={shippingData.phone}
                            onChange={handleShippingChange}
                            className="w-full pl-10 pr-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                          />
                        </div>
                      </div>
                    </div>
  
                    <div>
                      <label className="block text-sm font-medium text-infinity-yellow mb-2">
                        Dirección *
                      </label>
                      <div className="relative">
                        <MapPin size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          name="address"
                          required
                          value={shippingData.address}
                          onChange={handleShippingChange}
                          placeholder="Av. Principal 123, Dpto. 4B"
                          className="w-full pl-10 pr-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                        />
                      </div>
                    </div>
  
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-infinity-yellow mb-2">
                          Distrito *
                        </label>
                        <select
                          name="district"
                          required
                          value={shippingData.district}
                          onChange={handleShippingChange}
                          className="w-full px-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                        >
                          <option value="">Seleccionar distrito</option>
                          {districts.map(district => (
                            <option key={district} value={district}>{district}</option>
                          ))}
                        </select>
                      </div>
  
                      <div>
                        <label className="block text-sm font-medium text-infinity-yellow mb-2">
                          Ciudad *
                        </label>
                        <input
                          type="text"
                          name="city"
                          required
                          value={shippingData.city}
                          onChange={handleShippingChange}
                          className="w-full px-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                        />
                      </div>
  
                      <div>
                        <label className="block text-sm font-medium text-infinity-yellow mb-2">
                          Código Postal
                        </label>
                        <input
                          type="text"
                          name="postalCode"
                          value={shippingData.postalCode}
                          onChange={handleShippingChange}
                          className="w-full px-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                        />
                      </div>
                    </div>
  
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(2)}
                        className="btn-primary"
                        disabled={!shippingData.firstName || !shippingData.lastName || !shippingData.email || !shippingData.phone || !shippingData.address || !shippingData.district}
                      >
                        Continuar al Pago
                      </button>
                    </div>
                  </form>
                </div>
              )}
  
              {/* Paso 2: Información de Pago */}
              {currentStep === 2 && (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <CreditCard size={24} className="text-infinity-purple" />
                    <h2 className="text-2xl font-bold text-infinity-black">Método de Pago</h2>
                  </div>
  
                  <form className="space-y-6">
                    {/* Método de Pago */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-4">
                        Selecciona tu método de pago
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="method"
                            value="card"
                            checked={paymentData.method === 'card'}
                            onChange={handlePaymentChange}
                            className="h-4 w-4 text-infinity-purple focus:ring-infinity-purple"
                          />
                          <CreditCard size={20} className="ml-3 text-gray-600" />
                          <span className="ml-3 font-medium">Tarjeta de Crédito/Débito</span>
                        </label>
                        
                        <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="method"
                            value="bank"
                            checked={paymentData.method === 'bank'}
                            onChange={handlePaymentChange}
                            className="h-4 w-4 text-infinity-purple focus:ring-infinity-purple"
                          />
                          <span className="ml-3 text-lg">🏦</span>
                          <span className="ml-3 font-medium">Transferencia Bancaria</span>
                        </label>
                        
                        <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="method"
                            value="cash"
                            checked={paymentData.method === 'cash'}
                            onChange={handlePaymentChange}
                            className="h-4 w-4 text-infinity-purple focus:ring-infinity-purple"
                          />
                          <span className="ml-3 text-lg">💵</span>
                          <span className="ml-3 font-medium">Pago Contra Entrega</span>
                        </label>
                      </div>
                    </div>
  
                    {/* Información de Tarjeta */}
                    {paymentData.method === 'card' && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Número de Tarjeta *
                          </label>
                          <input
                            type="text"
                            name="cardNumber"
                            required
                            value={paymentData.cardNumber}
                            onChange={handlePaymentChange}
                            placeholder="1234 5678 9012 3456"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-infinity-purple focus:border-transparent"
                          />
                        </div>
  
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nombre en la Tarjeta *
                          </label>
                          <input
                            type="text"
                            name="cardName"
                            required
                            value={paymentData.cardName}
                            onChange={handlePaymentChange}
                            placeholder="Como aparece en la tarjeta"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-infinity-purple focus:border-transparent"
                          />
                        </div>
  
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Fecha de Vencimiento *
                            </label>
                            <input
                              type="text"
                              name="expiryDate"
                              required
                              value={paymentData.expiryDate}
                              onChange={handlePaymentChange}
                              placeholder="MM/AA"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-infinity-purple focus:border-transparent"
                            />
                          </div>
  
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              CVV *
                            </label>
                            <input
                              type="text"
                              name="cvv"
                              required
                              value={paymentData.cvv}
                              onChange={handlePaymentChange}
                              placeholder="123"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-infinity-purple focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>
                    )}
  
                    {/* Información de Transferencia */}
                    {paymentData.method === 'bank' && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-2">Datos para Transferencia</h4>
                        <div className="text-sm text-blue-800 space-y-1">
                          <p><strong>Banco:</strong> BCP</p>
                          <p><strong>Cuenta:</strong> 123-456789-012</p>
                          <p><strong>CCI:</strong> 002-123-001456789012-34</p>
                          <p><strong>Titular:</strong> InfinityWits S.A.C.</p>
                        </div>
                      </div>
                    )}
  
                    {/* Información de Pago Contra Entrega */}
                    {paymentData.method === 'cash' && (
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-900 mb-2">Pago Contra Entrega</h4>
                        <p className="text-sm text-green-800">
                          Podrás pagar en efectivo al momento de recibir tu pedido. 
                          Disponible solo en Lima Metropolitana.
                        </p>
                      </div>
                    )}
  
                    <div className="flex justify-between">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(1)}
                        className="btn-secondary"
                      >
                        Volver
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(3)}
                        className="btn-primary"
                      >
                        Revisar Pedido
                      </button>
                    </div>
                  </form>
                </div>
              )}
  
              {/* Paso 3: Confirmación */}
              {currentStep === 3 && (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <Shield size={24} className="text-infinity-purple" />
                    <h2 className="text-2xl font-bold text-infinity-black">Confirmación del Pedido</h2>
                  </div>
  
                  {/* Resumen de Envío */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-infinity-black mb-3">Dirección de Envío</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-medium">{shippingData.firstName} {shippingData.lastName}</p>
                      <p>{shippingData.address}</p>
                      <p>{shippingData.district}, {shippingData.city}</p>
                      <p>{shippingData.phone} | {shippingData.email}</p>
                    </div>
                  </div>
  
                  {/* Resumen de Pago */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-infinity-black mb-3">Método de Pago</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      {paymentData.method === 'card' && (
                        <p>Tarjeta terminada en ****{paymentData.cardNumber.slice(-4)}</p>
                      )}
                      {paymentData.method === 'bank' && (
                        <p>Transferencia Bancaria - BCP</p>
                      )}
                      {paymentData.method === 'cash' && (
                        <p>Pago Contra Entrega</p>
                      )}
                    </div>
                  </div>
  
                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="btn-secondary"
                    >
                      Volver
                    </button>
                    <button
                      onClick={handleSubmitOrder}
                      disabled={isProcessing}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProcessing ? 'Procesando...' : `Confirmar Pedido - ${formatPrice(finalTotal)}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
  
            {/* Resumen del Pedido */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-lg p-6 sticky top-4">
                <h3 className="text-lg font-bold text-infinity-black mb-4">Resumen del Pedido</h3>
                
                {/* Productos */}
                <div className="space-y-3 mb-6">
                  {items.map((item) => (
                    <div key={item.product.id} className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                        <span className="text-xs">IMG</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.product.name}</p>
                        <p className="text-xs text-gray-600">Cantidad: {item.quantity}</p>
                      </div>
                      <p className="font-medium text-sm">{formatPrice(item.product.price * item.quantity)}</p>
                    </div>
                  ))}
                </div>
  
                {/* Totales */}
                <div className="space-y-2 border-t pt-4">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Envío</span>
                    <span className="text-green-600">Gratis</span>
                  </div>
                  <div className="flex justify-between">
                    <span>IGV (18%)</span>
                    <span>{formatPrice(tax)}</span>
                  </div>
                  <hr />
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-infinity-purple">{formatPrice(finalTotal)}</span>
                  </div>
                </div>
  
                {/* Seguridad */}
                <div className="mt-6 text-center text-sm text-gray-500">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Shield size={16} className="text-green-500" />
                    <span>Pago 100% seguro</span>
                  </div>
                  <p>Tus datos están protegidos con encriptación SSL</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  export default Checkout;